import React from 'react';
import { Zip3Datum } from '../stats';
import { US_MAP_VIEWBOX, US_STATE_OUTLINES } from '../usMapOutline';
import { zip3Centroid } from '../zip3Centroids';
import { Currency, Money } from 'rpc/model/squareup/connect/v2/common/money';
import { formatConnectMoney } from 'utils/currency';

type UsSpendMapProps = {
  data: Record<string, Zip3Datum>;
  currency: Currency;
};

const STATE_FILL = '#EDEFF2';
const STATE_STROKE = '#FFFFFF';
const BUBBLE_FILL = '#006AFF';
const MIN_R = 3;
const MAX_R = 26;

type Bubble = Zip3Datum & { x: number; y: number };

const UsSpendMap: React.FC<UsSpendMapProps> = ({ data, currency }) => {
  // Resolve each ZIP3 group to a map coordinate; drop any we can't place.
  const bubbles: Bubble[] = Object.values(data)
    .map((d) => {
      const c = zip3Centroid(d.zip3);
      return c ? { ...d, x: c.x, y: c.y } : undefined;
    })
    .filter((b): b is Bubble => b !== undefined);

  const max = Math.max(...bubbles.map((b) => b.amount), 0);

  if (bubbles.length === 0 || max === 0) {
    return (
      <p className="paragraph-20 text-grey my-0">
        No purchases with a recognized US merchant location.
      </p>
    );
  }

  // Area-proportional radius (sqrt) so a bubble twice the spend reads as twice
  // the ink, not four times. Draw largest first so small dots stay clickable.
  const radiusFor = (amount: number): number =>
    MIN_R + Math.sqrt(amount / max) * (MAX_R - MIN_R);

  const ordered = [...bubbles].sort((a, b) => b.amount - a.amount);

  return (
    <div>
      <svg
        viewBox={US_MAP_VIEWBOX}
        className="w-full max-w-[560px] mx-auto block"
        role="img"
        aria-label="Spending by US ZIP code area"
      >
        <g>
          {US_STATE_OUTLINES.map((state) => (
            <path
              key={state.name}
              d={state.d}
              fill={STATE_FILL}
              stroke={STATE_STROKE}
              strokeWidth={0.75}
            />
          ))}
        </g>
        <g>
          {ordered.map((b) => {
            const money = formatConnectMoney(
              Money.create({ amount: b.amount, currency })
            );
            return (
              <circle
                key={b.zip3}
                cx={b.x}
                cy={b.y}
                r={radiusFor(b.amount)}
                fill={BUBBLE_FILL}
                fillOpacity={0.55}
                stroke={BUBBLE_FILL}
                strokeWidth={1}
              >
                <title>
                  {`ZIP ${b.zip3}xx — ${money} (${b.orderCount} ${
                    b.orderCount === 1 ? 'order' : 'orders'
                  })`}
                </title>
              </circle>
            );
          })}
        </g>
      </svg>
      <p className="paragraph-20 text-grey text-center mt-3 mb-0">
        Each dot is a ZIP code area; larger dots mean more spend.
      </p>
    </div>
  );
};

export default UsSpendMap;
